/**
 * LaTeX utility functions and helpers
 */

/**
 * Normalize LaTeX shorthand to canonical braced form
 * Converts \frac12 to \frac{1}{2}, \sqrt2 to \sqrt{2}, x^2 to x^{2}, etc.
 */
export const normalizeLatex = (latex: string): string => {
  let result = latex;

  // Normalize \frac - convert shorthand to braced form
  // \fracXY -> \frac{X}{Y} where X and Y are single tokens
  // \fracX{...} -> \frac{X}{...}
  // \frac{...}Y -> \frac{...}{Y}
  result = result.replace(
    /\\frac(?:\{([^}]+)\}|([a-zA-Z0-9]))(?:\{([^}]+)\}|([a-zA-Z0-9]))/g,
    (_, braceNum, charNum, braceDen, charDen) => {
      const num = braceNum || charNum;
      const den = braceDen || charDen;
      return `\\frac{${num}}{${den}}`;
    }
  );

  // Normalize \sqrt - convert shorthand to braced form
  // \sqrtX -> \sqrt{X} where X is a single token (but not if already braced or indexed)
  result = result.replace(
    /\\sqrt(?!\[)(?!\{)([a-zA-Z0-9])/g,
    '\\sqrt{$1}'
  );

  // Normalize exponents - convert x^Y to x^{Y} for single tokens
  result = result.replace(
    /\^(?!\{)([a-zA-Z0-9])/g,
    '^{$1}'
  );

  // Normalize subscripts - convert x_Y to x_{Y} for single tokens
  result = result.replace(
    /_(?!\{)([a-zA-Z0-9])/g,
    '_{$1}'
  );

  return result;
};

/**
 * Clean and format LaTeX string
 */
export const formatLatex = (latex: string): string => {
  return latex
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/\s*([+\-*/=<>])\s*/g, ' $1 ') // Add spaces around operators
    .replace(/\s+/g, ' ') // Clean up any double spaces created
    .trim();
};

/**
 * Convert simple text expressions to LaTeX
 */
export const convertToLatex = (text: string): string => {
  return text
    .replace(/\^(\w+)/g, '^{$1}') // Convert x^2 to x^{2}
    .replace(/_(\w+)/g, '_{$1}') // Convert x_1 to x_{1}
    .replace(/sqrt\(([^)]+)\)/g, '\\sqrt{$1}') // Convert sqrt(x) to \sqrt{x}
    .replace(/frac\(([^,]+),([^)]+)\)/g, '\\frac{$1}{$2}') // Convert frac(a,b) to \frac{a}{b}
    .replace(/\binfinity\b/g, '\\infty') // Convert infinity to \infty
    .replace(/\balpha\b/g, '\\alpha') // Convert alpha to \alpha
    .replace(/\bbeta\b/g, '\\beta') // Convert beta to \beta
    .replace(/\bgamma\b/g, '\\gamma') // Convert gamma to \gamma
    .replace(/\bdelta\b/g, '\\delta') // Convert delta to \delta
    .replace(/\bpi\b/g, '\\pi') // Convert pi to \pi
    .replace(/\btheta\b/g, '\\theta') // Convert theta to \theta
    .replace(/\<=\b/g, '\\leq') // Convert <= to \leq
    .replace(/\>=\b/g, '\\geq') // Convert >= to \geq
    .replace(/\!=\b/g, '\\neq') // Convert != to \neq
    .replace(/\+\-/g, '\\pm') // Convert +- to \pm
    .replace(/\*/g, '\\cdot'); // Convert * to \cdot
};

/**
 * Extract complexity level from LaTeX expression
 */
export const getComplexity = (latex: string): 'simple' | 'medium' | 'complex' => {
  const complexityFactors = {
    commands: (latex.match(/\\/g) || []).length,
    nested: (latex.match(/\{[^{}]*\{/g) || []).length,
    fractions: (latex.match(/\\frac/g) || []).length,
    integrals: (latex.match(/\\int/g) || []).length,
    matrices: (latex.match(/\\begin\{.*matrix.*\}/g) || []).length,
    length: latex.length,
  };

  const score = complexityFactors.commands * 2 +
                complexityFactors.nested * 3 +
                complexityFactors.fractions * 2 +
                complexityFactors.integrals * 3 +
                complexityFactors.matrices * 5 +
                Math.floor(complexityFactors.length / 20);

  if (score <= 5) return 'simple';
  if (score <= 15) return 'medium';
  return 'complex';
};

/**
 * Get user-friendly error messages
 */
export const getFriendlyErrorMessage = (error: string): string => {
  const errorMap: Record<string, string> = {
    'Expression cannot be empty': 'Please enter a mathematical expression',
    'unmatched opening braces': 'Check that all opening braces { have matching closing braces }',
    'unmatched closing braces': 'Check that all closing braces } have matching opening braces {',
    'unmatched opening parentheses': 'Check that all opening parentheses ( have matching closing parentheses )',
    'unmatched closing parentheses': 'Check that all closing parentheses ) have matching opening parentheses (',
  };

  for (const [pattern, message] of Object.entries(errorMap)) {
    if (error.toLowerCase().includes(pattern.toLowerCase())) {
      return message;
    }
  }

  return error; // Return original error if no friendly version found
};